import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FolderTree } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { Modal } from '@/components/common/Modal';
import { TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { catalogueReferenceApi } from '@/services/api/endpoints/library';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { BookCategory } from '@/types/library';
import { Badge } from './shared';

/**
 * How the collection is arranged.
 *
 * <p>Categories nest — Science → Physics — and the full path is stored on the
 * row rather than walked at read time, so a listing of two hundred categories is
 * one query and not two hundred.
 *
 * <p>Retiring rather than deleting, for the usual reason: books point at their
 * category, and a school that reorganises its shelves should not lose the record
 * of where a book used to live.
 */
export function CategoriesPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const manages = can(PERMISSIONS.CATALOGUE_MANAGE);

  const [editing, setEditing] = useState<BookCategory | null>(null);
  const [adding, setAdding] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');

  const categories = useQuery({
    queryKey: queryKeys.catalogueReference.categories,
    queryFn: () => catalogueReferenceApi.categories(),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.catalogueReference.categories });
    void queryClient.invalidateQueries({ queryKey: queryKeys.books.all });
  };

  function open(category: BookCategory | null) {
    setEditing(category);
    setAdding(category === null);
    setCode(category?.code ?? '');
    setName(category?.name ?? '');
    setParentId(category?.parentId ?? '');
  }

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        code: code.trim(),
        name: name.trim(),
        parentId: parentId === '' ? null : parentId,
      };
      return editing === null
        ? catalogueReferenceApi.createCategory(payload)
        : catalogueReferenceApi.updateCategory(editing.id, payload);
    },
    onSuccess: (category) => {
      toast.success(`${category.fullPath} saved`);
      setAdding(false);
      setEditing(null);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, next }: { id: string; next: 'ACTIVE' | 'INACTIVE' }) =>
      catalogueReferenceApi.setCategoryStatus(id, next),
    onSuccess: () => {
      toast.success('Category updated');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const columns: Column<BookCategory>[] = [
    {
      key: 'name',
      header: 'Category',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{row.fullPath}</span>
          <span className="block font-mono text-[11px] text-subtle">{row.code}</span>
        </span>
      ),
    },
    { key: 'bookCount', header: 'Titles', className: 'text-right', render: (row) => row.bookCount },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Badge tone={row.status === 'ACTIVE' ? 'success' : 'neutral'}>
          {row.status === 'ACTIVE' ? 'in use' : 'retired'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) =>
        manages ? (
          <span className="flex justify-end gap-1.5">
            <Button size="sm" variant="secondary" onClick={() => open(row)}>
              Edit
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                setStatus.mutate({
                  id: row.id,
                  next: row.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
                })
              }
            >
              {row.status === 'ACTIVE' ? 'Retire' : 'Restore'}
            </Button>
          </span>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title="Categories"
        description="How the collection is arranged on the shelves."
        icon={<FolderTree className="h-5 w-5" aria-hidden="true" />}
        action={manages ? <Button onClick={() => open(null)}>Add a category</Button> : undefined}
      />

      <Card>
        <DataTable
          columns={columns}
          rows={categories.data}
          rowKey={(row) => row.id}
          loading={categories.isPending}
          error={categories.error}
          onRetry={() => void categories.refetch()}
          emptyTitle="No categories"
          emptyDescription="Every school starts with a set. If this is empty, something went wrong provisioning it."
        />
      </Card>

      <Modal
        open={adding || editing !== null}
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
        title={editing === null ? 'Add a category' : editing.fullPath}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setAdding(false);
                setEditing(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => save.mutate()}
              disabled={code.trim() === '' || name.trim() === '' || save.isPending}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <TextField
            label="Code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            required
            hint="Short and stable. An import or a shelf label names it."
          />
          <TextField
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
          />
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-[0.04em] text-muted">
              Sits under
            </span>
            <select
              className="mt-1 w-full border border-line bg-bg px-3 py-2 text-sm text-ink"
              value={parentId}
              onChange={(event) => setParentId(event.target.value)}
            >
              <option value="">Nothing — a top-level category</option>
              {(categories.data ?? [])
                .filter((row) => row.id !== editing?.id)
                .map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.fullPath}
                  </option>
                ))}
            </select>
          </label>
        </div>
      </Modal>
    </>
  );
}
